import React, { useState, useEffect, useMemo } from 'react';
import { Card } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import { Checkbox } from '@/components/ui/checkbox';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from '@/components/ui/dialog';
import {
  Shield, Search, Loader2, Save, History as HistoryIcon,
  AlertTriangle, CheckCircle2, XCircle,
} from 'lucide-react';
import api, { formatErrorMessage } from '../utils/api';
import { toast } from 'sonner';
import { esSuperadmin, isSocio } from '../utils/permissions';

const CAP_LABELS = {
  superadmin: 'Superadmin',
  rrhh_avanzado: 'RRHH Avanzado',
  gestor_gastos: 'Gestor Gastos',
  gestor_comercial: 'Gestor Comercial',
  aprobador_caja_chica: 'Aprobador Caja Chica',
  supervisor_caja_chica: 'Supervisor Caja Chica',
};

export default function PermisosMatrix({ currentUser }) {
  const [catalogo, setCatalogo] = useState({ permisos: [], roles: [], areas: [] });
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [editing, setEditing] = useState(null);
  const [auditOpen, setAuditOpen] = useState(false);

  const puedeVer = isSocio(currentUser);
  const puedeEditar = esSuperadmin(currentUser);

  const load = async () => {
    if (!puedeVer) { setLoading(false); return; }
    setLoading(true);
    try {
      const [c, u] = await Promise.all([
        api.get('/permisos/catalogo'),
        api.get('/permisos/usuarios'),
      ]);
      setCatalogo(c.data);
      setUsers(u.data.items || []);
    } catch (err) {
      toast.error(formatErrorMessage(err));
    } finally { setLoading(false); }
  };

  useEffect(() => { load(); }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const filtered = useMemo(() => {
    const q = (search || '').toLowerCase().trim();
    if (!q) return users;
    return users.filter(u =>
      (u.name || '').toLowerCase().includes(q) ||
      (u.email || '').toLowerCase().includes(q) ||
      (u.role || '').toLowerCase().includes(q) ||
      (u.areas || []).some(a => a.toLowerCase().includes(q)) ||
      (u.permisos_finanzas || []).some(p => p.toLowerCase().includes(q))
    );
  }, [users, search]);

  if (!puedeVer) {
    return (
      <Card className="p-8 text-center border-amber-200 bg-amber-50">
        <Shield className="w-10 h-10 text-amber-500 mx-auto mb-2" />
        <p className="text-sm text-amber-800">Solo Socios pueden ver la matriz de permisos.</p>
      </Card>
    );
  }

  return (
    <div className="space-y-4" data-testid="permisos-matrix">
      <Card className="p-4">
        <div className="flex items-center justify-between flex-wrap gap-3">
          <div className="flex items-center gap-3">
            <Shield className="w-5 h-5 text-indigo-600" />
            <div>
              <h3 className="text-sm font-bold text-slate-900">Matriz de Permisos y Capacidades</h3>
              <p className="text-[11px] text-slate-500">
                Permisos granulares que se suman al rol y área. Solo Superadmin puede modificarlos.
              </p>
            </div>
          </div>
          <div className="flex items-center gap-2">
            <div className="relative">
              <Search className="absolute left-2 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-slate-400" />
              <Input
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                placeholder="Buscar nombre, email, rol, permiso…"
                className="pl-7 h-8 w-64 text-xs"
                data-testid="permisos-search"
              />
            </div>
            <Button variant="outline" size="sm" onClick={() => setAuditOpen(true)} className="h-8" data-testid="permisos-audit-btn">
              <HistoryIcon className="w-3.5 h-3.5 mr-1" /> Auditoría
            </Button>
          </div>
        </div>
      </Card>

      {loading ? (
        <Card className="p-8 flex items-center justify-center"><Loader2 className="w-6 h-6 animate-spin text-indigo-500" /></Card>
      ) : (
        <Card className="p-0 overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full text-xs">
              <thead className="bg-slate-100 text-slate-700">
                <tr>
                  <th className="text-left px-3 py-2">Funcionario</th>
                  <th className="text-left px-3 py-2">Rol</th>
                  <th className="text-left px-3 py-2">Áreas</th>
                  <th className="text-left px-3 py-2">Permisos Explícitos</th>
                  <th className="text-left px-3 py-2">Capacidades Efectivas</th>
                  {puedeEditar && <th className="px-3 py-2"></th>}
                </tr>
              </thead>
              <tbody>
                {filtered.map(u => {
                  const capsActivas = Object.entries(u.capacidades || {}).filter(([, v]) => v).map(([k]) => k);
                  return (
                    <tr key={u.id} className="border-t border-slate-100 hover:bg-slate-50" data-testid={`permisos-row-${u.email}`}>
                      <td className="px-3 py-2">
                        <div className="font-semibold text-slate-800">{u.name}</div>
                        <div className="text-[10px] text-slate-400">{u.email}</div>
                      </td>
                      <td className="px-3 py-2">
                        <Badge className={`text-[10px] ${
                          u.role === 'Administrador' ? 'bg-red-100 text-red-700' :
                          u.role === 'Líder' ? 'bg-blue-100 text-blue-700' :
                          u.role === 'Coordinador' ? 'bg-emerald-100 text-emerald-700' :
                          'bg-slate-100 text-slate-700'
                        }`}>{u.role}</Badge>
                      </td>
                      <td className="px-3 py-2 text-slate-600">
                        {(u.areas || []).map(a => (
                          <span key={a} className="inline-block text-[10px] px-1.5 py-0.5 mr-1 mb-0.5 bg-slate-100 rounded">{a}</span>
                        ))}
                      </td>
                      <td className="px-3 py-2">
                        {(u.permisos_finanzas || []).length === 0 ? (
                          <span className="text-slate-400 text-[10px]">—</span>
                        ) : (u.permisos_finanzas || []).map(p => (
                          <span key={p} className="inline-block text-[10px] px-1.5 py-0.5 mr-1 mb-0.5 bg-indigo-100 text-indigo-700 rounded font-mono">
                            {p}
                          </span>
                        ))}
                      </td>
                      <td className="px-3 py-2">
                        {capsActivas.length === 0 ? (
                          <span className="text-slate-400 text-[10px]">—</span>
                        ) : capsActivas.map(k => (
                          <span key={k} className="inline-flex items-center text-[10px] px-1.5 py-0.5 mr-1 mb-0.5 bg-emerald-50 text-emerald-700 rounded">
                            <CheckCircle2 className="w-2.5 h-2.5 mr-0.5" />
                            {CAP_LABELS[k] || k}
                          </span>
                        ))}
                      </td>
                      {puedeEditar && (
                        <td className="px-3 py-2 text-right">
                          <Button size="sm" variant="outline" className="h-7 text-xs" onClick={() => setEditing(u)} data-testid={`permisos-edit-${u.email}`}>
                            Editar
                          </Button>
                        </td>
                      )}
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </Card>
      )}

      {editing && (
        <EditPermisosDialog
          usuario={editing}
          catalogo={catalogo}
          onClose={() => setEditing(null)}
          onSaved={() => { setEditing(null); load(); }}
        />
      )}
      {auditOpen && <AuditoriaDialog onClose={() => setAuditOpen(false)} />}
    </div>
  );
}


function EditPermisosDialog({ usuario, catalogo, onClose, onSaved }) {
  const [seleccionados, setSeleccionados] = useState(new Set(usuario.permisos_finanzas || []));
  const [motivo, setMotivo] = useState('');
  const [saving, setSaving] = useState(false);

  const toggle = (key) => {
    const s = new Set(seleccionados);
    if (s.has(key)) s.delete(key); else s.add(key);
    setSeleccionados(s);
  };

  const guardar = async () => {
    setSaving(true);
    try {
      await api.patch(`/permisos/usuarios/${usuario.id}`, {
        permisos: Array.from(seleccionados),
        motivo: motivo || null,
      });
      toast.success('Permisos actualizados');
      onSaved();
    } catch (err) {
      toast.error(formatErrorMessage(err));
    } finally { setSaving(false); }
  };

  return (
    <Dialog open onOpenChange={onClose}>
      <DialogContent className="max-w-lg" data-testid="permisos-edit-dialog">
        <DialogHeader>
          <DialogTitle>Permisos de {usuario.name}</DialogTitle>
        </DialogHeader>
        <div className="space-y-3 text-sm">
          <div className="text-xs bg-slate-50 p-2 rounded">
            <div><strong>Email:</strong> {usuario.email}</div>
            <div><strong>Rol:</strong> {usuario.role} · <strong>Áreas:</strong> {(usuario.areas || []).join(', ') || '—'}</div>
          </div>
          <div className="space-y-2">
            {catalogo.permisos.map(p => {
              const peligroso = p.key === 'superadmin_root';
              return (
                <label key={p.key} className={`flex items-start gap-2 cursor-pointer p-2 rounded hover:bg-slate-50 ${peligroso ? 'border border-rose-200 bg-rose-50' : ''}`}>
                  <Checkbox
                    checked={seleccionados.has(p.key)}
                    onCheckedChange={() => toggle(p.key)}
                    data-testid={`permisos-check-${p.key}`}
                  />
                  <div className="flex-1">
                    <div className="text-xs font-semibold flex items-center gap-1">
                      {peligroso && <AlertTriangle className="w-3 h-3 text-rose-600" />}
                      <span className="font-mono">{p.key}</span>
                    </div>
                    <div className="text-[11px] text-slate-500">{p.label}</div>
                  </div>
                </label>
              );
            })}
          </div>
          <div>
            <Label className="text-xs">Motivo del cambio (opcional)</Label>
            <Input value={motivo} onChange={(e) => setMotivo(e.target.value)} className="h-8" data-testid="permisos-motivo" />
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose} disabled={saving}>Cancelar</Button>
          <Button onClick={guardar} disabled={saving} className="bg-indigo-600 hover:bg-indigo-700" data-testid="permisos-guardar-btn">
            {saving ? <Loader2 className="w-4 h-4 animate-spin mr-1" /> : <Save className="w-4 h-4 mr-1" />}
            Guardar cambios
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}


function AuditoriaDialog({ onClose }) {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    (async () => {
      try {
        const r = await api.get('/permisos/auditoria');
        setItems(r.data.items || []);
      } catch (err) {
        toast.error(formatErrorMessage(err));
      } finally { setLoading(false); }
    })();
  }, []);

  return (
    <Dialog open onOpenChange={onClose}>
      <DialogContent className="max-w-3xl" data-testid="permisos-audit-dialog">
        <DialogHeader><DialogTitle>Auditoría de cambios de permisos</DialogTitle></DialogHeader>
        <div className="max-h-[60vh] overflow-auto text-xs">
          {loading ? (
            <div className="p-6 text-center"><Loader2 className="w-5 h-5 animate-spin mx-auto text-indigo-500" /></div>
          ) : items.length === 0 ? (
            <p className="text-center text-slate-500 py-8">Sin eventos registrados.</p>
          ) : (
            <table className="w-full">
              <thead className="bg-slate-100 sticky top-0">
                <tr>
                  <th className="text-left px-2 py-1.5">Fecha</th>
                  <th className="text-left px-2 py-1.5">Target</th>
                  <th className="text-left px-2 py-1.5">Agregados</th>
                  <th className="text-left px-2 py-1.5">Removidos</th>
                  <th className="text-left px-2 py-1.5">Por</th>
                  <th className="text-left px-2 py-1.5">Motivo</th>
                </tr>
              </thead>
              <tbody>
                {items.map(a => (
                  <tr key={a.id} className="border-t border-slate-100">
                    <td className="px-2 py-1.5 whitespace-nowrap">{new Date(a.at).toLocaleString('es-CL', { dateStyle: 'short', timeStyle: 'short' })}</td>
                    <td className="px-2 py-1.5">{a.target_name}</td>
                    <td className="px-2 py-1.5">
                      {(a.diff?.agregados || []).map(p => <span key={p} className="inline-block text-[10px] px-1 mr-0.5 bg-emerald-100 text-emerald-700 rounded font-mono">+{p}</span>)}
                    </td>
                    <td className="px-2 py-1.5">
                      {(a.diff?.removidos || []).map(p => <span key={p} className="inline-block text-[10px] px-1 mr-0.5 bg-rose-100 text-rose-700 rounded font-mono">-{p}</span>)}
                    </td>
                    <td className="px-2 py-1.5">{a.by_name}</td>
                    <td className="px-2 py-1.5 text-slate-500">{a.motivo || '—'}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
        <DialogFooter><Button variant="outline" onClick={onClose}>Cerrar</Button></DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
